/**
 * ForkChainButton — inline button on locked/published chains. Authenticated
 * users can fork the chain, opening a private copy where they may modify
 * one or more steps with better sources, then optionally submit for review.
 *
 * Lead's brief (final sprint, Apr 26 2026):
 *   "This enables the teacher engagement loop — a teacher forks a chain,
 *   modifies a step with better sources, submits it for review."
 *
 * Methodology hold #4: forks ALWAYS attribute the original. The Fork
 * dialog reminds the user before creation that attribution is automatic
 * and non-removable.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function ForkChainButton({ chainId, chainTitle, era }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onFork = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await api.post(`/forks/${chainId}`);
      const fork = r.data?.fork;
      if (fork?.id) {
        navigate(`/forks/${fork.id}`);
      } else {
        setError('Fork not created.');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Fork failed.');
    }
    setBusy(false);
  };

  return (
    <div className="flex items-center gap-2">
      {error && (
        <span className="font-mono text-[10px] text-red-300" data-testid="fork-error">{error}</span>
      )}
      <button
        onClick={onFork}
        disabled={busy}
        data-testid="fork-chain-btn"
        title={`Fork "${chainTitle}" — make a private copy you can modify and submit for review.`}
        className="font-hud text-[10px] tracking-widest inline-flex items-center gap-1.5 border px-2.5 py-1 hover:bg-white/5 disabled:opacity-50 transition-colors"
        style={{ color: era?.primary || '#D4A84A', borderColor: `${era?.primary || '#D4A84A'}55` }}
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <GitBranch size={11} />}
        FORK
      </button>
    </div>
  );
}
